import { Form } from 'antd';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TAssetViewResponse, TUpdateAssetDetailRequest } from '@models/asset';
import { TCommonError } from '@models/common';
import { EUserRoles } from '@models/user';
import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import { assetApi } from '@services/assetApi';
import { appToast } from '@utils/appToast';
import { handleShowToastError } from '@utils/helpers';
import {
  EXCLUDED_ASSET_TYPE,
  FIXED_ASSET_DETAIL_CONSTANT,
  LOCKED_STATUSES,
  MOCK_LOCATIONS,
  STATUS_CODE_TO_LABEL,
  STATUS_LABEL_TO_CODE,
  TAssetDetail,
  TAssetStatus,
  TChangedField,
} from '../constant';

const { MESSAGE, NAME } = FIXED_ASSET_DETAIL_CONSTANT;

const SUCCESS_TOAST_DURATION = 2; // seconds — MSG-PC005-S01

// ─── date helpers ─────────────────────────────────────────────────────────────

/** Convert ISO date/datetime string → "YYYY/M/D" (JP display format). */
function formatIsoToJpDate(isoStr: string | null | undefined): string {
  if (!isoStr) {
    return '—';
  }
  const datePart = isoStr.substring(0, 10); // "2015-11-30"
  const parts = datePart.split('-');
  if (parts.length < 3) {
    return isoStr;
  }
  return `${parts[0]}/${parts[1]}/${parts[2]}`;
}

// ─── response mapper ───────────────────────────────────────────────────────────

/**
 * Map GET /api/v1/assets/{assetId} response → local TAssetDetail shape.
 * Field-by-field mapping per api-integration.md §2.1.
 * sealRequestQuantity: BE-PENDING (BE-1+BE-2) — defaults to 0 when absent.
 *
 * Split into mapReadOnlyFields / mapEditableFields to keep complexity low.
 */

/** Read-only fields [4]-[13] of the mapping — split out to keep mapper complexity low. */
function mapReadOnlyFields(
  data: TAssetViewResponse
): Pick<
  TAssetDetail,
  | 'assetId'
  | 'assetNo'
  | 'company'
  | 'store'
  | 'assetType'
  | 'acquisitionDate'
  | 'usefulLife'
  | 'acquisitionCost'
  | 'bookValueStart'
  | 'updatedAt'
  | 'hasPhoto'
> {
  const storeParts = [data.storeCode, data.storeName].filter(Boolean);
  const store = storeParts.length > 0 ? storeParts.join(' ') : '—';

  const companyParts = [data.companyOriginCode, data.companyOriginName].filter(
    Boolean
  );
  const company = companyParts.length > 0 ? companyParts.join('_') : '—';

  return {
    assetId: data.assetId ?? '',
    assetNo: data.assetCode ?? '',
    company,
    store,
    assetType: data.assetType ?? '',
    acquisitionDate: formatIsoToJpDate(data.purchaseDate),
    usefulLife: data.usefulLife ?? 0,
    acquisitionCost: data.purchasePrice ?? null,
    bookValueStart: data.bookValueStart ?? null,
    updatedAt: formatIsoToJpDate(data.latestUpdatedAt),
    hasPhoto: data.hasPhoto ?? false,
  };
}

/** Editable fields [14]-[19] of the mapping — split out to keep mapper complexity low. */
function mapEditableFields(
  data: TAssetViewResponse
): Pick<
  TAssetDetail,
  | 'assetName'
  | 'department'
  | 'departmentCode'
  | 'location'
  | 'quantity'
  | 'sealRequest'
  | 'remarks'
> {
  return {
    assetName: data.assetName ?? '',
    department: data.departmentName ?? '',
    departmentCode: data.departmentCode ?? '',
    location: data.location ?? '',
    quantity: data.quantity ?? null,
    // BE-PENDING (BE-1 + BE-2): sealRequestQuantity absent from response until deployed → default 0.
    sealRequest: data.sealRequestQuantity ?? 0,
    remarks: data.note ?? '',
  };
}

function mapApiToAssetDetail(data: TAssetViewResponse): TAssetDetail {
  const statusLabel: TAssetStatus =
    STATUS_CODE_TO_LABEL[data.statusCode ?? ''] ?? '未確認';

  return {
    ...mapReadOnlyFields(data),
    ...mapEditableFields(data),
    status: statusLabel,
    source: data.source ?? null,
  };
}

// ─── validation-error parser ──────────────────────────────────────────────────

/**
 * Parse 400 validation errors from BE → inline field error map.
 * Matches description text against known MSG codes (contract §4).
 */
function parseBeValidationErrors(
  errors: TCommonError[]
): Record<string, string> {
  const result: Record<string, string> = {};
  errors.forEach((err) => {
    if (err.description === MESSAGE.V01) {
      result[NAME.ASSET_NAME] = err.description;
    } else if (err.description === MESSAGE.V02) {
      result[NAME.QUANTITY] = err.description;
    } else if (err.description === MESSAGE.V03) {
      result[NAME.SEAL_REQUEST] = err.description;
    } else {
      // no-op: intentionally no default action for unrecognized error codes
    }
  });
  return result;
}

/**
 * Validate an optional numeric form field: empty/undefined/null is valid (no error);
 * otherwise it must be an integer >= min. Returns the error message, or undefined.
 */
function validateNumericField(
  rawVal: unknown,
  min: number,
  message: string
): string | undefined {
  if (rawVal === undefined || rawVal === null || String(rawVal).trim() === '') {
    return undefined;
  }
  const num = Number(rawVal);
  if (!Number.isInteger(num) || num < min) {
    return message;
  }
  return undefined;
}

/**
 * Compare a form field's current value against the loaded asset's value.
 * Returns a TChangedField entry when different, otherwise null.
 */
function diffField(
  label: string,
  assetValue: unknown,
  formValue: unknown
): TChangedField | null {
  const current = String(assetValue ?? '');
  const next = String(formValue ?? '');
  return current !== next ? { label, value: next } : null;
}

const isRawValuePresent = (rawVal: unknown): boolean =>
  rawVal !== undefined && rawVal !== null && String(rawVal).trim() !== '';

/** Parse a raw form value into a number, falling back when blank/absent. */
function parseOptionalNumber<TFallback>(
  rawVal: unknown,
  fallback: TFallback
): number | TFallback {
  return isRawValuePresent(rawVal) ? Number(rawVal) : fallback;
}

/** Omit targetStatus (null) when locked → no status change sent to BE. */
function resolveTargetStatus(
  isStatusLocked: boolean,
  targetStatus: TAssetStatus
): string | null {
  return isStatusLocked ? null : STATUS_LABEL_TO_CODE[targetStatus] ?? null;
}

/** Build the PUT /api/v1/assets/{assetId} request body from current form values. */
function buildUpdateRequestBody(
  values: Record<string, unknown>,
  isStatusLocked: boolean,
  targetStatus: TAssetStatus,
  deptOptions: { label: string; value: string }[]
): TUpdateAssetDetailRequest {
  const deptCode = (values[NAME.DEPARTMENT] as string | undefined) ?? null;
  const deptOption = deptOptions.find((o) => o.value === deptCode);
  return {
    assetName: String(values[NAME.ASSET_NAME] ?? '').trim(),
    departmentCode: deptCode || null,
    departmentName: deptOption?.label ?? null,
    location: (values[NAME.LOCATION] as string | undefined) ?? null,
    quantity: parseOptionalNumber(values[NAME.QUANTITY], null),
    sealRequestQuantity: parseOptionalNumber(values[NAME.SEAL_REQUEST], 0),
    remarks: (values[NAME.REMARKS] as string | undefined) ?? null,
    targetStatus: resolveTargetStatus(isStatusLocked, targetStatus),
  };
}

type TSaveOutcome =
  | { kind: 'success' }
  | { kind: 'validationErrors'; errors: Record<string, string> }
  | { kind: 'forbidden'; message?: string }
  | { kind: 'genericError'; error?: unknown };

/**
 * Call PUT /api/v1/assets/{assetId} and classify the result.
 */
async function performUpdateAssetDetail(
  assetId: string,
  requestBody: TUpdateAssetDetailRequest
): Promise<TSaveOutcome> {
  try {
    const response = await assetApi.updateAssetDetail(assetId, requestBody);
    if (response.ok) {
      return { kind: 'success' };
    }
    if (response.errors?.length) {
      // ok:false with validation errors — map to inline fields
      const beErrors = parseBeValidationErrors(
        response.errors as TCommonError[]
      );
      if (Object.keys(beErrors).length > 0) {
        return { kind: 'validationErrors', errors: beErrors };
      }
    }
    // Non-field business error, or ok:false with no errors — MSG-PC005-E02
    return { kind: 'genericError' };
  } catch (innerError: any) {
    // 400 with body → parse inline validation errors
    if (innerError?.status === 400 && innerError?.data?.errors?.length) {
      const beErrors = parseBeValidationErrors(
        innerError.data.errors as TCommonError[]
      );
      if (Object.keys(beErrors).length > 0) {
        return { kind: 'validationErrors', errors: beErrors };
      }
    }
    // 403 — asset is excluded from inventory scope (e.g. 資産除去債務): show BE's message instead
    if (innerError?.status === 403) {
      const message = (
        innerError?.data?.errors as TCommonError[] | undefined
      )?.[0]?.description;
      return { kind: 'forbidden', message };
    }
    // Real error (including a genuine 404 when the asset is not found) — MSG-PC005-E02
    return { kind: 'genericError', error: innerError };
  }
}

// ─── hook ─────────────────────────────────────────────────────────────────────

function useFixedAssetLedgerDetail() {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { role } = useContext(AppContext);
  const isAdmin = (role ?? []).includes(EUserRoles.ADMIN);

  const [asset, setAsset] = useState<TAssetDetail | null>(null);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [targetStatus, setTargetStatus] = useState<TAssetStatus>('未確認');
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Dropdown options: loaded from GET /api/v1/assets/lookups.
  // BE-PENDING (BE-8): falls back to mock if endpoint unavailable.
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [locationOptions, setLocationOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const isStatusLocked = asset
    ? LOCKED_STATUSES.includes(asset.status) ||
      asset.source === 'UNLISTED' ||
      !isAdmin
    : false;

  // Out-of-inventory-scope asset type: ADMIN/AREA_OWNER can still open this page to view it,
  // but every field + Save must stay disabled (BE rejects the update regardless of role).
  const isAssetTypeLocked = asset?.assetType === EXCLUDED_ASSET_TYPE;

  // ── load lookups (部門 / 所属 dropdowns) ────────────────────────────────────
  const loadLookups = useCallback(async () => {
    try {
      const response = await assetApi.getAssetLookups();
      if (response.ok && response.data) {
        setDepartmentOptions(
          (response.data.departments ?? [])
            .filter((d) => d.departmentCode)
            .map((d) => ({
              label: d.departmentName ?? d.departmentCode,
              value: d.departmentCode,
            }))
        );
        setLocationOptions(
          (response.data.locations ?? []).map((l) => ({ label: l, value: l }))
        );
      } else {
        setLocationOptions(MOCK_LOCATIONS);
      }
    } catch {
      setLocationOptions(MOCK_LOCATIONS);
    }
  }, []);

  // ── load asset detail ────────────────────────────────────────────────────────
  const loadAsset = useCallback(async () => {
    if (!assetId) {
      return;
    }
    setLoadingPage(true);
    try {
      // GET /api/v1/assets/{assetId} — real endpoint, 8/9 fields ready.
      const response = await assetApi.getAssetById(assetId);
      if (response.ok && response.data) {
        const mapped = mapApiToAssetDetail(response.data);
        setAsset(mapped);
        setTargetStatus(mapped.status);
        form.setFieldsValue({
          [NAME.ASSET_NAME]: mapped.assetName,
          [NAME.DEPARTMENT]: mapped.departmentCode || undefined,
          [NAME.LOCATION]: mapped.location || undefined,
          [NAME.QUANTITY]: mapped.quantity,
          [NAME.SEAL_REQUEST]: mapped.sealRequest,
          [NAME.REMARKS]: mapped.remarks,
        });
      } else {
        // ok:false from API — MSG-PC005-E01
        appToast.error({ message: MESSAGE.E01 });
      }
    } catch (error: any) {
      // 403 — asset's store is outside the caller's scope (AREA_OWNER/STORE_OWNER): no access,
      // redirect to home instead of showing the detail page/toast.
      if (error?.status === 403) {
        navigate('/');
        return;
      }
      // 5xx / network / timeout — MSG-PC005-E01
      appToast.error({ message: MESSAGE.E01 });
      handleShowToastError(error);
    } finally {
      setLoadingPage(false);
    }
  }, [assetId, form, navigate]);

  useEffect(() => {
    loadLookups();
    loadAsset();
  }, [loadLookups, loadAsset]);

  // ── navigation ───────────────────────────────────────────────────────────────

  const handleBack = useCallback(() => {
    navigate('/fixedassetledger');
  }, [navigate]);

  const handleNavigateToPhoto = useCallback(() => {
    if (asset?.hasPhoto) {
      navigate(`/fixedassetledger/photo/${asset.assetId}`);
    }
  }, [asset, navigate]);

  // ── client-side validation ───────────────────────────────────────────────────

  const handleClickSave = useCallback(() => {
    if (isAssetTypeLocked) {
      appToast.error({ message: MESSAGE.E03 });
      return;
    }

    const values = form.getFieldsValue();
    const errors: Record<string, string> = {};

    if (!String(values[NAME.ASSET_NAME] ?? '').trim()) {
      errors[NAME.ASSET_NAME] = MESSAGE.V01;
    }

    const qtyError = validateNumericField(
      values[NAME.QUANTITY],
      1,
      MESSAGE.V02
    );
    if (qtyError) {
      errors[NAME.QUANTITY] = qtyError;
    }

    const sealError = validateNumericField(
      values[NAME.SEAL_REQUEST],
      0,
      MESSAGE.V03
    );
    if (sealError) {
      errors[NAME.SEAL_REQUEST] = sealError;
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length === 0) {
      setShowSaveDialog(true);
    }
  }, [form, isAssetTypeLocked]);

  // ── changed-fields diff ──────────────────────────────────────────────────────

  const getChangedFields = useCallback((): TChangedField[] => {
    if (!asset) {
      return [];
    }
    const values = form.getFieldsValue();
    const deptLabel = (code: unknown) =>
      departmentOptions.find((o) => o.value === code)?.label ??
      String(code ?? '');

    const fieldDiffs = [
      diffField('資産名称', asset.assetName, values[NAME.ASSET_NAME]),
      diffField(
        '部門',
        deptLabel(asset.departmentCode),
        deptLabel(values[NAME.DEPARTMENT])
      ),
      diffField('所属', asset.location, values[NAME.LOCATION]),
      diffField('数量', asset.quantity, values[NAME.QUANTITY]),
      diffField('タグ要求', asset.sealRequest, values[NAME.SEAL_REQUEST]),
      diffField('備考', asset.remarks, values[NAME.REMARKS]),
    ].filter((field): field is TChangedField => field !== null);

    const statusChanged = !isStatusLocked && targetStatus !== asset.status;
    const statusDiff: TChangedField[] = statusChanged
      ? [{ label: 'ステータス', value: `${asset.status} → ${targetStatus}` }]
      : [];

    return [...fieldDiffs, ...statusDiff];
  }, [asset, departmentOptions, form, isStatusLocked, targetStatus]);

  // ── confirm save → PUT /api/v1/assets/{assetId} ─────────────────────────────

  const handleConfirmSave = useCallback(async () => {
    if (!asset || !assetId || loadingSave) {
      return;
    }

    const values = form.getFieldsValue();
    const requestBody = buildUpdateRequestBody(
      values,
      isStatusLocked,
      targetStatus,
      departmentOptions
    );

    setLoadingSave(true);
    try {
      // BE-PENDING (BE-3 through BE-7): PUT /api/v1/assets/{assetId} not yet created.
      // Call will fail (404/405) until BE-3~BE-7 deploy.
      // When BE is ready, remove the catch-mock block below and let the real response flow.
      const outcome = await performUpdateAssetDetail(assetId, requestBody);

      if (outcome.kind === 'validationErrors') {
        setValidationErrors(outcome.errors);
        setShowSaveDialog(false);
      } else if (outcome.kind === 'forbidden') {
        appToast.error({ message: outcome.message || MESSAGE.E03 });
        setShowSaveDialog(false);
      } else if (outcome.kind === 'genericError') {
        appToast.error({ message: MESSAGE.E02 });
        if (outcome.error) {
          handleShowToastError(outcome.error);
        }
      } else {
        setShowSaveDialog(false);
        appToast.success({
          message: MESSAGE.S01,
          duration: SUCCESS_TOAST_DURATION,
        });
        navigate('/fixedassetledger');
      }
    } finally {
      setLoadingSave(false);
    }
  }, [
    asset,
    assetId,
    departmentOptions,
    form,
    isStatusLocked,
    loadingSave,
    navigate,
    targetStatus,
  ]);

  return {
    state: {
      asset,
      loadingPage,
      loadingSave,
      showSaveDialog,
      targetStatus,
      validationErrors,
      isStatusLocked,
      isAssetTypeLocked,
      form,
      departmentOptions,
      locationOptions,
    },
    handler: {
      handleBack,
      handleNavigateToPhoto,
      handleClickSave,
      handleConfirmSave,
      setShowSaveDialog,
      setTargetStatus,
      getChangedFields,
    },
  };
}

export default useFixedAssetLedgerDetail;
